import React, { useState, useEffect } from "react";
import { getDatabase, ref, onValue } from "firebase/database";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import app from "../../../firebaseConfig";

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend);

function GraficaTendenciaPorDia() {
  const [ventasPorDia, setVentasPorDia] = useState({});
  const [rangoTiempo, setRangoTiempo] = useState("Todo");

  useEffect(() => {
    const db = getDatabase(app);

    // ✅ NUEVAS rutas
    const ordenesRef = ref(db, "nature/Awoo/Ordenes");
    const ordenesPagadasRef = ref(db, "nature/Awoo/OrdenesPagadas");

    const procesarDatos = (data, ventasDiarias) => {
      const ahora = new Date();

      Object.values(data).forEach((pedido) => {
        if (pedido.fecha && pedido.cantidad) {
          // convertir fecha
          const fechaStr = pedido.fecha.trim().split(",")[0]; // por si viene con hora
          const [dia, mes, año] = fechaStr.split("/").map(Number);
          if (!dia || !mes || !año) return;

          const fechaPedido = new Date(año, mes - 1, dia);

          // determinar si entra según rango
          let incluirPedido = false;
          const diasDiferencia = (ahora - fechaPedido) / (1000 * 60 * 60 * 24);

          switch (rangoTiempo) {
            case "1 semana":
              incluirPedido = diasDiferencia <= 7;
              break;
            case "2 semanas":
              incluirPedido = diasDiferencia <= 14;
              break;
            case "1 mes":
              incluirPedido = diasDiferencia <= 30;
              break;
            case "6 meses":
              incluirPedido = diasDiferencia <= 180;
              break;
            case "1 año":
              incluirPedido = diasDiferencia <= 365;
              break;
            default:
              incluirPedido = true;
          }

          // acumular si cumple rango
          if (incluirPedido) {
            ventasDiarias[fechaStr] = (ventasDiarias[fechaStr] || 0) + pedido.cantidad;
          }
        }
      });
    };

    const cargarDatos = () => {
      let ventasDiarias = {};

      onValue(ordenesRef, (snap1) => {
        const data1 = snap1.val() || {};
        procesarDatos(data1, ventasDiarias);

        onValue(ordenesPagadasRef, (snap2) => {
          const data2 = snap2.val() || {};
          procesarDatos(data2, ventasDiarias);
          setVentasPorDia(ventasDiarias);
        });
      });
    };

    cargarDatos();
  }, [rangoTiempo]);

  const obtenerDiaSemana = (fecha) => {
    const [dia, mes, año] = fecha.split("/").map(Number);
    const fechaObj = new Date(año, mes - 1, dia);
    const diasSemana = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
    return diasSemana[fechaObj.getDay()];
  };

  const datosGrafica = () => {
    const fechasOrdenadas = Object.keys(ventasPorDia)
      .filter((fecha) => /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(fecha))
      .sort((a, b) => {
        const [diaA, mesA, añoA] = a.split("/").map(Number);
        const [diaB, mesB, añoB] = b.split("/").map(Number);
        return new Date(añoA, mesA - 1, diaA) - new Date(añoB, mesB - 1, diaB);
      });

    const etiquetasConDia = fechasOrdenadas.map((f) => `${obtenerDiaSemana(f)} (${f})`);
    const cantidades = fechasOrdenadas.map((f) => ventasPorDia[f]);
    const totalCompras = cantidades.reduce((a, v) => a + v, 0);
    const porcentajes = cantidades.map((c) => ((c / totalCompras) * 100).toFixed(2));

    return {
      labels: etiquetasConDia,
      datasets: [
        {
          label: "Cantidad de productos vendidos",
          data: cantidades,
          borderColor: "#4CAF50",
          backgroundColor: "rgba(76, 175, 80, 0.3)",
          borderWidth: 2,
          pointBackgroundColor: "#4CAF50",
          yAxisID: "cantidad",
        },
        {
          label: "Porcentaje de ventas",
          data: porcentajes,
          borderColor: "#FFD700",
          backgroundColor: "rgba(255, 215, 0, 0.3)",
          borderWidth: 2,
          pointBackgroundColor: "#FFD700",
          yAxisID: "porcentaje",
        }
      ]
    };
  };

  const opcionesGrafica = {
    responsive: true,
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const cantidad = ctx.raw;
            return ctx.dataset.label === "Cantidad de productos vendidos"
              ? `${cantidad} unidades`
              : `${cantidad}% del total`;
          }
        }
      },
      legend: {
        labels: { color: "#fff" }
      }
    },
    scales: {
      cantidad: {
        type: "linear",
        position: "left",
        title: { display: true, text: "Cantidad Vendida", color: "#fff" },
        ticks: { color: "#fff" },
        grid: { color: "rgba(255,255,255,0.2)" }
      },
      porcentaje: {
        type: "linear",
        position: "right",
        title: { display: true, text: "Porcentaje", color: "#fff" },
        ticks: { color: "#fff", callback: (v) => `${v}%` },
        grid: { drawOnChartArea: false }
      }
    }
  };

  return (
    <div style={{ padding: "20px", backgroundColor: "#111", color: "#fff" }}>
      <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
        📈 Tendencia de Ventas por Día
      </h2>

      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <label htmlFor="tiempo" style={{ fontSize: "18px", marginRight: "10px" }}>
          Filtrar por tiempo:
        </label>
        <select
          id="tiempo"
          onChange={(e) => setRangoTiempo(e.target.value)}
          value={rangoTiempo}
          style={{
            padding: "10px",
            fontSize: "16px",
            backgroundColor: "#222",
            color: "#fff",
            borderRadius: "5px"
          }}
        >
          {["Todo", "1 semana", "2 semanas", "1 mes", "6 meses", "1 año"].map((rango) => (
            <option key={rango} value={rango}>
              {rango}
            </option>
          ))}
        </select>
      </div>

      <div style={{ maxWidth: "80%", margin: "0 auto" }}>
        <Line data={datosGrafica()} options={opcionesGrafica} />
      </div>
    </div>
  );
}

export default GraficaTendenciaPorDia;
